import React, { useState, useEffect, useMemo, useRef } from 'react'
import { AppDialog, Button, Select, type SelectOption, MiniLoader } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  listFamilyEduPtmTeachers,
  listFamilyEduPtmSlots,
  bookEduPtmSlot,
  rescheduleEduPtmSlot,
  cancelEduPtmBooking,
  mapEduPtmError,
  isSlotClashingWithOtherBookings,
  type EduPtmDayItem,
  type EduPtmSlotItem,
  type FamilyEduPtmTeacherItem,
} from '../../services/eduPtmService'
import { generateEduRequestId } from '../../services/eduEventService'
import { utcIsoToZonedParts, DEFAULT_EDU_TIMEZONE } from '../../utils/eduZonedTime'
import { formatEduDate } from '../../utils/eduFamilyFormat'
import type { FamilyLearnerRow } from '../../services/eduFamilyService'

export interface OtherChildBooking {
  learnerId: string
  slotStartMs: number
  slotEndMs: number
}

export interface FamilyPtmBookingDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  learnerId: string
  day: EduPtmDayItem | null
  learner?: FamilyLearnerRow
  otherLearnerBookings?: OtherChildBooking[]
  otherLearners?: FamilyLearnerRow[]
  onBookingChanged?: () => void
}

const isCutoffTimePassed = (cutoffAt?: string | null): boolean => {
  if (!cutoffAt) return true
  return Date.now() > new Date(cutoffAt).getTime()
}

const isBookingTimeOpen = (openAt?: string | null, closeAt?: string | null): boolean => {
  if (!openAt || !closeAt) return false
  const now = Date.now()
  return now >= new Date(openAt).getTime() && now <= new Date(closeAt).getTime()
}

export const FamilyPtmBookingDialog: React.FC<FamilyPtmBookingDialogProps> = ({
  isOpen,
  onClose,
  workspaceId,
  learnerId,
  day,
  learner,
  otherLearnerBookings = [],
  otherLearners = [],
  onBookingChanged,
}) => {
  const { t, locale } = useNls()
  const effectiveTz = day?.timezone || DEFAULT_EDU_TIMEZONE

  // Main state
  const [teachers, setTeachers] = useState<FamilyEduPtmTeacherItem[]>([])
  const [loadingTeachers, setLoadingTeachers] = useState(false)
  const [selectedTeacherUid, setSelectedTeacherUid] = useState<string>('')

  const [slots, setSlots] = useState<EduPtmSlotItem[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [selectedSlotStart, setSelectedSlotStart] = useState<string>('')

  // Confirmation step for booking
  const [pendingSlotToBook, setPendingSlotToBook] = useState<EduPtmSlotItem | null>(null)
  const [isBooking, setIsBooking] = useState(false)

  // Move / Reschedule mode
  const [isMoveMode, setIsMoveMode] = useState(false)
  const [moveSlotStart, setMoveSlotStart] = useState<string>('')
  const [isMoving, setIsMoving] = useState(false)

  // Cancel mode
  const [isCancelMode, setIsCancelMode] = useState(false)
  const [cancelReason, setCancelReason] = useState<string>('')
  const [isCancelling, setIsCancelling] = useState(false)

  // Error banners inside dialog
  const [dialogError, setDialogError] = useState<string | null>(null)

  const requestIdRef = useRef(generateEduRequestId('ptm_family_book'))

  const isMinorSelf = Boolean(learner?.isMinor && learner?.relation === 'SELF')

  // Booking window & cutoff checks
  const isBookingOpen = isBookingTimeOpen(day?.bookingOpenAt, day?.bookingCloseAt)
  const isCutoffPassed = isCutoffTimePassed(day?.rescheduleCutoffAt)

  // Fetch teachers when dialog opens
  useEffect(() => {
    if (!isOpen || !workspaceId || !learnerId || !day) return
    let active = true
    void Promise.resolve().then(() => {
      if (active) {
        setLoadingTeachers(true)
        setDialogError(null)
      }
    })

    listFamilyEduPtmTeachers({ workspaceId, learnerId, dayId: day.id })
      .then((res) => {
        if (!active) return
        const list = res?.teachers || []
        setTeachers(list)
        if (list.length > 0) {
          // Default to the first teacher with an existing booking, or first teacher
          const teacherWithBooking = list.find((t) => t.booking && t.booking.status === 'BOOKED')
          setSelectedTeacherUid(teacherWithBooking ? teacherWithBooking.teacherUid : list[0].teacherUid)
        }
      })
      .catch((err) => {
        if (!active) return
        setDialogError(t(mapEduPtmError(err)))
      })
      .finally(() => {
        if (active) setLoadingTeachers(false)
      })

    return () => {
      active = false
    }
  }, [isOpen, workspaceId, learnerId, day, t])

  const currentTeacher = useMemo(() => {
    return teachers.find((t) => t.teacherUid === selectedTeacherUid) || null
  }, [teachers, selectedTeacherUid])

  const bookedSlotWithTeacher = useMemo(() => {
    if (!currentTeacher?.booking || currentTeacher.booking.status !== 'BOOKED') return null
    return currentTeacher.booking
  }, [currentTeacher])

  // Fetch slots when selected teacher changes
  useEffect(() => {
    if (!isOpen || !workspaceId || !learnerId || !day || !selectedTeacherUid) return
    let active = true
    void Promise.resolve().then(() => {
      if (active) {
        setLoadingSlots(true)
        setDialogError(null)
        setPendingSlotToBook(null)
        setIsMoveMode(false)
        setIsCancelMode(false)
      }
    })

    listFamilyEduPtmSlots({ workspaceId, learnerId, dayId: day.id, teacherUid: selectedTeacherUid })
      .then((res) => {
        if (!active) return
        const slotItems = res?.slots || []
        setSlots(slotItems)
        const free = slotItems.filter((s) => !s.isBreak && (!s.booking || s.booking.status === 'CANCELLED'))
        if (free.length > 0) {
          setSelectedSlotStart(free[0].slotStart)
          setMoveSlotStart(free[0].slotStart)
        }
      })
      .catch((err) => {
        if (!active) return
        setDialogError(t(mapEduPtmError(err)))
      })
      .finally(() => {
        if (active) setLoadingSlots(false)
      })

    return () => {
      active = false
    }
  }, [isOpen, workspaceId, learnerId, day, selectedTeacherUid, t])

  // Free slots options for Select
  const freeSlotOptions = useMemo<SelectOption<string>[]>(() => {
    return slots
      .filter((s) => !s.isBreak && (!s.booking || s.booking.status === 'CANCELLED'))
      .map((s) => {
        const startParts = utcIsoToZonedParts(s.slotStart, effectiveTz)
        const endParts = utcIsoToZonedParts(s.slotEnd, effectiveTz)
        return {
          value: s.slotStart,
          label: `${startParts.time} – ${endParts.time}`,
        }
      })
  }, [slots, effectiveTz])

  // Dynamic fetch of other children's bookings if otherLearners prop provided
  const [internalOtherBookings, setInternalOtherBookings] = useState<OtherChildBooking[]>([])
  useEffect(() => {
    if (!isOpen || !day || !otherLearners || otherLearners.length === 0) return
    let active = true
    Promise.all(
      otherLearners.map(async (ol) => {
        try {
          const res = await listFamilyEduPtmTeachers({
            workspaceId: ol.workspaceId,
            learnerId: ol.learnerId,
            dayId: day.id,
          })
          const bookings: OtherChildBooking[] = []
          for (const tc of res?.teachers || []) {
            if (tc.booking && tc.booking.status === 'BOOKED') {
              bookings.push({
                learnerId: ol.learnerId,
                slotStartMs: new Date(tc.booking.slotStart).getTime(),
                slotEndMs: new Date(tc.booking.slotEnd).getTime(),
              })
            }
          }
          return bookings
        } catch {
          return []
        }
      }),
    ).then((items) => {
      if (!active) return
      setInternalOtherBookings(items.flat())
    })
    return () => {
      active = false
    }
  }, [isOpen, day, otherLearners])

  const allOtherBookings = useMemo(() => {
    return [...otherLearnerBookings, ...internalOtherBookings]
  }, [otherLearnerBookings, internalOtherBookings])

  // Clash check helper
  const isSlotClashingWithOtherChild = (slotStartIso: string): boolean => {
    if (!slotStartIso || allOtherBookings.length === 0) return false
    const slot = slots.find((s) => s.slotStart === slotStartIso)
    return isSlotClashingWithOtherBookings(
      slot || { slotStart: slotStartIso, slotDurationMin: currentTeacher?.slotDurationMin },
      allOtherBookings,
    )
  }

  const isSelectedSlotClashing = isSlotClashingWithOtherChild(selectedSlotStart)
  const isMoveSlotClashing = isSlotClashingWithOtherChild(moveSlotStart)

  const handleStartBooking = () => {
    setDialogError(null)
    if (!selectedSlotStart) return
    if (isSelectedSlotClashing) {
      setDialogError(t('NLSFamilyPtmTwoChildClashWarning'))
      return
    }
    const foundSlot = slots.find((s) => s.slotStart === selectedSlotStart)
    if (!foundSlot) return
    setPendingSlotToBook(foundSlot)
  }

  const handleConfirmBooking = async () => {
    if (!day || !currentTeacher || !pendingSlotToBook) return
    setDialogError(null)
    setIsBooking(true)

    try {
      await bookEduPtmSlot({
        workspaceId,
        dayId: day.id,
        teacherUid: currentTeacher.teacherUid,
        slotStart: pendingSlotToBook.slotStart,
        learnerId,
        requestId: requestIdRef.current,
      })
      setPendingSlotToBook(null)
      onBookingChanged?.()
      // Refresh teacher & slot data
      const res = await listFamilyEduPtmTeachers({ workspaceId, learnerId, dayId: day.id })
      setTeachers(res?.teachers || [])
      const slotsRes = await listFamilyEduPtmSlots({
        workspaceId,
        learnerId,
        dayId: day.id,
        teacherUid: currentTeacher.teacherUid,
      })
      setSlots(slotsRes?.slots || [])
    } catch (err) {
      setDialogError(t(mapEduPtmError(err)))
    } finally {
      setIsBooking(false)
    }
  }

  const handleConfirmMove = async () => {
    if (!day || !bookedSlotWithTeacher || !moveSlotStart) return
    setDialogError(null)
    if (isMoveSlotClashing) {
      setDialogError(t('NLSFamilyPtmTwoChildClashWarning'))
      return
    }
    setIsMoving(true)

    try {
      await rescheduleEduPtmSlot({
        workspaceId,
        bookingId: bookedSlotWithTeacher.id,
        newSlotStart: moveSlotStart,
        requestId: generateEduRequestId('ptm_move'),
      })
      setIsMoveMode(false)
      onBookingChanged?.()
      const res = await listFamilyEduPtmTeachers({ workspaceId, learnerId, dayId: day.id })
      setTeachers(res?.teachers || [])
      const slotsRes = await listFamilyEduPtmSlots({
        workspaceId,
        learnerId,
        dayId: day.id,
        teacherUid: selectedTeacherUid,
      })
      setSlots(slotsRes?.slots || [])
    } catch (err) {
      setDialogError(t(mapEduPtmError(err)))
    } finally {
      setIsMoving(false)
    }
  }

  const handleConfirmCancel = async () => {
    if (!day || !bookedSlotWithTeacher) return
    const reason = cancelReason.trim()
    if (!reason) {
      setDialogError(t('NLSEduPtmCancelDayReasonLabel'))
      return
    }
    setDialogError(null)
    setIsCancelling(true)

    try {
      await cancelEduPtmBooking({
        workspaceId,
        bookingId: bookedSlotWithTeacher.id,
        reason,
      })
      setIsCancelMode(false)
      setCancelReason('')
      onBookingChanged?.()
      const res = await listFamilyEduPtmTeachers({ workspaceId, learnerId, dayId: day.id })
      setTeachers(res?.teachers || [])
      const slotsRes = await listFamilyEduPtmSlots({
        workspaceId,
        learnerId,
        dayId: day.id,
        teacherUid: selectedTeacherUid,
      })
      setSlots(slotsRes?.slots || [])
    } catch (err) {
      setDialogError(t(mapEduPtmError(err)))
    } finally {
      setIsCancelling(false)
    }
  }

  if (!day) return null

  const isOnlineMeeting = day.meetingType === 'ONLINE' || Boolean(currentTeacher?.isOnline)
  const roomOrLocation = currentTeacher?.roomOrOnline || day.location || (isOnlineMeeting ? 'Online' : 'School')

  const formatTimeRange = (startIso: string, endIso: string): string => {
    const sParts = utcIsoToZonedParts(startIso, effectiveTz)
    const eParts = utcIsoToZonedParts(endIso, effectiveTz)
    return `${sParts.time} – ${eParts.time}`
  }

  const teacherOptions: SelectOption<string>[] = teachers.map((tc) => ({
    value: tc.teacherUid,
    label: tc.booking && tc.booking.status === 'BOOKED'
      ? `${tc.teacherName} (Booked)`
      : tc.teacherName,
  }))

  return (
    <AppDialog
      open={isOpen}
      onDismiss={onClose}
      title={day.title}
      size="md"
      actions={[
        {
          label: t('NLSEduPtmCancelDay'),
          onClick: onClose,
          variant: 'secondary',
        },
      ]}
    >
      <div className="familyClassTab__subtabContent" data-testid="family-ptm-booking-dialog">
        {/* Error banner */}
        {dialogError && (
          <div className="familyClassTab__error" role="alert" data-testid="ptm-dialog-error">
            {dialogError}
          </div>
        )}

        {/* Read-only notice for minor self accounts */}
        {isMinorSelf && (
          <div
            className="familyEventDetail__banner"
            role="note"
            data-testid="ptm-minor-read-only-note"
          >
            {t('NLSFamilyPtmMinorReadOnlyNote')}
          </div>
        )}

        {/* Day metadata */}
        <div className="familyEventDetail__metaGrid">
          <div className="familyEventDetail__metaItem">
            <span className="familyEventDetail__metaLabel">{t('NLSEduPtmDayDateLabel')}</span>
            <span className="familyEventDetail__metaValue">{formatEduDate(day.date, locale, effectiveTz)}</span>
          </div>

          <div className="familyEventDetail__metaItem">
            <span className="familyEventDetail__metaLabel">{t('NLSFamilyPtmMeetingTypeLabel')}</span>
            <span className="familyEventDetail__metaValue">
              {isOnlineMeeting ? t('NLSEduPtmMeetingTypeOnline') : t('NLSEduPtmMeetingTypeInPerson')}
            </span>
          </div>

          <div className="familyEventDetail__metaItem">
            <span className="familyEventDetail__metaLabel">{t('NLSFamilyPtmRoomLabel')}</span>
            <span className="familyEventDetail__metaValue">{roomOrLocation}</span>
          </div>

          <div className="familyEventDetail__metaItem">
            <span className="familyEventDetail__metaLabel">{t('NLSEduPtmDayBookingOpenLabel')}</span>
            <span className="familyEventDetail__metaValue">
              {t('NLSFamilyPtmBookingOpenRange', {
                open: utcIsoToZonedParts(day.bookingOpenAt, effectiveTz).date,
                close: utcIsoToZonedParts(day.bookingCloseAt, effectiveTz).date,
              })}
            </span>
          </div>
        </div>

        {/* Cutoff notice */}
        <div className="familyEventDetail__metaItem">
          <span className="familyEventDetail__metaLabel">{t('NLSEduPtmDayCutoffLabel')}</span>
          <span className="familyEventDetail__metaValue">
            {t('NLSFamilyPtmCutoffLabel', {
              cutoff: `${utcIsoToZonedParts(day.rescheduleCutoffAt, effectiveTz).date} ${utcIsoToZonedParts(day.rescheduleCutoffAt, effectiveTz).time}`,
            })}
          </span>
        </div>

        {/* Teachers loader / picker */}
        {loadingTeachers ? (
          <div className="familyClassTab__loader" aria-label={t('NLSFamilyEventsLoading')}>
            <MiniLoader />
          </div>
        ) : teachers.length === 0 ? (
          <div className="familyClassTab__emptyState">
            <div className="familyClassTab__emptyTitle">{t('NLSEduPtmEmptySlots')}</div>
          </div>
        ) : (
          <div className="familyClassTab__subtabContent">
            <div data-testid="family-ptm-teacher-select-wrap">
              <label
                htmlFor="family-ptm-teacher-select"
                className="familyEventDetail__metaLabel"
                style={{ display: 'block', marginBottom: 4 }}
              >
                {t('NLSFamilyPtmSelectTeacher')}
              </label>
              <Select
                id="family-ptm-teacher-select"
                ariaLabel={t('NLSFamilyPtmSelectTeacher')}
                value={selectedTeacherUid}
                onChange={(val) => setSelectedTeacherUid(val)}
                options={teacherOptions}
              />
            </div>

            {/* Currently booked slot with selected teacher */}
            {bookedSlotWithTeacher ? (
              <div
                className="familyClassTab__card familyClassTab__eventCard"
                data-testid="ptm-booked-slot-card"
                style={{ marginTop: 8 }}
              >
                <div className="familyClassTab__cardHeader">
                  <span className="familyClassTab__eventTitle">
                    {t('NLSFamilyPtmExistingBooking', {
                      time: formatTimeRange(bookedSlotWithTeacher.slotStart, bookedSlotWithTeacher.slotEnd),
                      status: bookedSlotWithTeacher.status,
                    })}
                  </span>
                </div>

                <div className="familyEventDetail__metaGrid" style={{ marginTop: 8 }}>
                  <div className="familyEventDetail__metaItem">
                    <span className="familyEventDetail__metaLabel">{t('NLSFamilyPtmTeacherLabel')}</span>
                    <span className="familyEventDetail__metaValue">{bookedSlotWithTeacher.teacherName}</span>
                  </div>
                  <div className="familyEventDetail__metaItem">
                    <span className="familyEventDetail__metaLabel">{t('NLSFamilyPtmRoomLabel')}</span>
                    <span className="familyEventDetail__metaValue">{roomOrLocation}</span>
                  </div>
                </div>

                {/* Actions on booked slot */}
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  {isOnlineMeeting && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          window.location.href = `/edu/ptm/join?workspaceId=${workspaceId}&bookingId=${bookedSlotWithTeacher.id}`
                        }
                      }}
                      data-testid="ptm-join-btn"
                    >
                      {t('NLSFamilyPtmJoinMeeting')}
                    </Button>
                  )}

                  {!isMinorSelf && !isCutoffPassed && !isMoveMode && !isCancelMode && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setIsMoveMode(true)
                          setIsCancelMode(false)
                          setDialogError(null)
                        }}
                        data-testid="ptm-move-btn"
                      >
                        {t('NLSEduPtmRescheduleButton')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setIsCancelMode(true)
                          setIsMoveMode(false)
                          setDialogError(null)
                        }}
                        data-testid="ptm-cancel-booking-btn"
                      >
                        {t('NLSEduPtmCancelBookingButton')}
                      </Button>
                    </>
                  )}
                </div>

                {/* Cutoff explanation */}
                {isCutoffPassed && (
                  <div
                    className="familyEventDetail__metaLabel"
                    style={{ marginTop: 8, color: 'var(--muted)' }}
                    data-testid="ptm-cutoff-passed-note"
                  >
                    {t('NLSFamilyPtmCutoffPassedDesc')}
                  </div>
                )}

                {/* Move Mode UI */}
                {isMoveMode && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      background: 'var(--surface-sunken)',
                      borderRadius: 'var(--radius-ctl-sm)',
                    }}
                    data-testid="ptm-move-section"
                  >
                    <label
                      htmlFor="family-ptm-move-select"
                      className="familyEventDetail__metaLabel"
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      {t('NLSEduPtmSelectNewSlotLabel')}
                    </label>
                    <div data-testid="ptm-move-select-wrap">
                      <Select
                        id="family-ptm-move-select"
                        ariaLabel={t('NLSEduPtmSelectNewSlotLabel')}
                        value={moveSlotStart}
                        onChange={(val) => {
                          setMoveSlotStart(val)
                          if (isSlotClashingWithOtherChild(val)) {
                            setDialogError(t('NLSFamilyPtmTwoChildClashWarning'))
                          } else {
                            setDialogError(null)
                          }
                        }}
                        options={freeSlotOptions}
                      />
                    </div>

                    {isMoveSlotClashing && (
                      <div
                        className="familyClassTab__error"
                        style={{ marginTop: 8 }}
                        role="alert"
                        data-testid="ptm-move-clash-warning"
                      >
                        {t('NLSFamilyPtmTwoChildClashWarning')}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleConfirmMove()}
                        disabled={isMoving || isMoveSlotClashing || freeSlotOptions.length === 0}
                        data-testid="ptm-confirm-move-btn"
                      >
                        {isMoving ? t('NLSEduPtmRescheduleConfirmButton') : t('NLSFamilyPtmMoveConfirmButton')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsMoveMode(false)}
                      >
                        {t('NLSFamilyPtmKeepMeetingButton')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Cancel Mode UI */}
                {isCancelMode && (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      background: 'var(--surface-sunken)',
                      borderRadius: 'var(--radius-ctl-sm)',
                    }}
                    data-testid="ptm-cancel-section"
                  >
                    <label
                      htmlFor="family-ptm-cancel-reason"
                      className="familyEventDetail__metaLabel"
                      style={{ display: 'block', marginBottom: 4 }}
                    >
                      {t('NLSFamilyPtmCancelReasonLabel')}
                    </label>
                    <input
                      id="family-ptm-cancel-reason"
                      type="text"
                      className="commonInput"
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-ctl-sm)',
                        border: '1px solid var(--border-subtle)',
                        background: 'var(--surface-card)',
                        color: 'var(--text-strong)',
                        fontSize: 13,
                        boxSizing: 'border-box',
                      }}
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder={t('NLSFamilyPtmCancelReasonLabel')}
                      data-testid="ptm-cancel-reason-input"
                    />

                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleConfirmCancel()}
                        disabled={isCancelling || !cancelReason.trim()}
                        data-testid="ptm-confirm-cancel-btn"
                      >
                        {isCancelling ? t('NLSEduPtmCancelDayConfirm') : t('NLSFamilyPtmCancelConfirmButton')}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsCancelMode(false)}
                      >
                        {t('NLSFamilyPtmKeepMeetingButton')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* No booking yet: Booking flow */
              <div style={{ marginTop: 8 }}>
                {!isBookingOpen ? (
                  <div
                    className="familyEventDetail__metaLabel"
                    style={{ color: 'var(--muted)', padding: '12px 0' }}
                    data-testid="ptm-booking-not-open-note"
                  >
                    {t('NLSFamilyPtmBookingNotOpenDesc')}
                  </div>
                ) : isMinorSelf ? (
                  /* Minor self accounts get no book buttons */
                  null
                ) : loadingSlots ? (
                  <div className="familyClassTab__loader" aria-label={t('NLSFamilyEventsLoading')}>
                    <MiniLoader />
                  </div>
                ) : freeSlotOptions.length === 0 ? (
                  <div className="familyClassTab__emptyState">
                    <div className="familyClassTab__emptyTitle">{t('NLSEduPtmEmptySlots')}</div>
                  </div>
                ) : (
                  <div>
                    {/* Confirmation step */}
                    {pendingSlotToBook ? (
                      <div
                        style={{
                          padding: 12,
                          background: 'var(--surface-sunken)',
                          borderRadius: 'var(--radius-ctl-sm)',
                        }}
                        data-testid="ptm-confirm-step"
                      >
                        <p style={{ margin: '0 0 10px 0', fontSize: 14, color: 'var(--text-strong)' }}>
                          {t('NLSFamilyPtmBookConfirmPrompt', {
                            teacherName: currentTeacher?.teacherName || '',
                            time: formatTimeRange(pendingSlotToBook.slotStart, pendingSlotToBook.slotEnd),
                          })}
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => void handleConfirmBooking()}
                            disabled={isBooking}
                            data-testid="ptm-confirm-booking-btn"
                          >
                            {isBooking ? t('NLSEduPtmBookConfirmButton') : t('NLSFamilyPtmBookSlot')}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setPendingSlotToBook(null)}
                            disabled={isBooking}
                          >
                            {t('NLSEduPtmCancelDay')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label
                          htmlFor="family-ptm-slot-select"
                          className="familyEventDetail__metaLabel"
                          style={{ display: 'block', marginBottom: 4 }}
                        >
                          {t('NLSFamilyPtmSelectSlot')}
                        </label>
                        <div data-testid="family-ptm-slot-select-wrap">
                          <Select
                            id="family-ptm-slot-select"
                            ariaLabel={t('NLSFamilyPtmSelectSlot')}
                            value={selectedSlotStart}
                            onChange={(val) => {
                              setSelectedSlotStart(val)
                              if (isSlotClashingWithOtherChild(val)) {
                                setDialogError(t('NLSFamilyPtmTwoChildClashWarning'))
                              } else {
                                setDialogError(null)
                              }
                            }}
                            options={freeSlotOptions}
                          />
                        </div>

                        {isSelectedSlotClashing && (
                          <div
                            className="familyClassTab__error"
                            style={{ marginTop: 8 }}
                            role="alert"
                            data-testid="ptm-book-clash-warning"
                          >
                            {t('NLSFamilyPtmTwoChildClashWarning')}
                          </div>
                        )}

                        <div style={{ marginTop: 12 }}>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={handleStartBooking}
                            disabled={!selectedSlotStart || isSelectedSlotClashing}
                            data-testid="family-ptm-start-book-btn"
                          >
                            {t('NLSFamilyPtmBookSlot')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppDialog>
  )
}

export default FamilyPtmBookingDialog
